import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@/core/lib/utils';
import {
  CountryCode,
  validateZipCode,
  normalizeZip,
  formatZipOnType,
  CountryCodes,
} from '@/core/utils'; // import formatter
import { Input } from './Input';

interface InputZipCodeProps {
  countryCode?: CountryCode; // ISO country code (e.g., 'US', 'CA', 'GB')
  value: string; // Controlled input value
  onChange: (value: string) => void; // Controlled change handler
  onValidChange?: (isValid: boolean, errorMessage: string) => void; // Emits validity changes upstream
  label?: string; // Optional label text
  placeholder?: string; // Optional placeholder
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * InputZipCode - country-aware postal/ZIP code input with validation.
 * - Validates on change and blur, emits validity via onValidChange.
 * - Shows an error message only after blur (touched) or when external error is passed.
 */

const InputZipCode = React.forwardRef<HTMLInputElement, InputZipCodeProps>(
  (
    {
      className,
      countryCode = 'US',
      onChange,
      onValidChange,
      value,
      placeholder = 'Enter postal code',
      disabled = false,
      required = false,
      ...props
    },
    ref
  ) => {
    // export const InputZipCode: React.FC<InputZipCodeProps> = ({
    //   countryCode = 'US',
    //   value,
    //   onChange,
    //   onValidChange,
    //   placeholder = 'Enter postal code',
    //   disabled = false,
    //   required = false,
    //   className = '',
    //   error,
    // }) => {
    const [touched, setTouched] = useState(false);
    const [internalError, setInternalError] = useState<string>('');

    // Normalize for validation; keep displayed input as formatted value
    const normalized = useMemo(
      () => normalizeZip(countryCode, value),
      [countryCode, value]
    );

    // Run validation when value or country changes
    useEffect(() => {
      const { valid, hint } = validateZipCode(countryCode, normalized);
      onValidChange?.(
        required ? valid && normalized.length > 0 : valid,
        internalError
      );

      // Show internal error only after first blur to avoid noisy UX while typing
      if (touched) {
        if (required && normalized.length === 0) {
          setInternalError('Postal code is required');
        } else {
          setInternalError(valid ? '' : hint || 'Invalid postal code');
        }
      }
    }, [
      countryCode,
      normalized,
      onValidChange,
      required,
      touched,
      internalError,
    ]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // Apply live formatting while typing using shared util
        const formatted = formatZipOnType(countryCode, e.target.value);
        onChange(formatted);
      },
      [countryCode, onChange]
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        // Format pasted content to avoid invalid characters/separators
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const formatted = formatZipOnType(countryCode, pasted);
        onChange(formatted);
      },
      [countryCode, onChange]
    );

    const handleBlur = useCallback(() => {
      setTouched(true);
      const { valid, hint } = validateZipCode(countryCode, normalized);
      if (required && normalized.length === 0) {
        setInternalError('Postal code is required');
      } else {
        setInternalError(valid ? '' : hint || 'Invalid postal code');
      }
    }, [countryCode, normalized, required]);

    // Use numeric keyboard where possible for better mobile UX
    const inputMode = useMemo(() => {
      return CountryCodes.includes(countryCode) ? 'numeric' : 'text';
    }, [countryCode]);

    return (
      <Input
        type='text'
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={inputMode}
        autoComplete='postal-code'
        className={className}
        ref={ref}
        {...props}
      />
    );
  }
);
export { InputZipCode };
